import { useMemo, useState } from 'react';

import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useComparisonMode } from '@/modules/analytics/hooks/useComparisonMode';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';

import { AnalyticsEmptyState } from './AnalyticsEmptyState';
import { CompareTrendCell } from './CompareTrendCell';
import {
  type KnowledgeBaseRow,
  useKnowledgeBasePerformance,
} from './hooks/useKnowledgeBasePerformance';
import { KnowledgeBasePerformanceExport } from './KnowledgeBasePerformanceExport';
import { formatDateRangeLabel } from '../../utils/formatDateRangeLabel';

type SortColumn = 'retrieved' | 'used';
type SortDirection = 'asc' | 'desc';

const numberFormatter = new Intl.NumberFormat('en-US');

const HeaderLabel = ({ label, className }: { label: string; className?: string }) => (
  <span
    className={cn(
      'inline-flex items-center text-sm leading-6 font-medium text-muted-foreground',
      className
    )}
  >
    {label}
  </span>
);

const SortIndicator = ({ active, direction }: { active: boolean; direction?: SortDirection }) => {
  if (!active) {
    return <ChevronsUpDownIcon className="size-3.5 text-muted-foreground" />;
  }
  const DirectionIcon = direction === 'asc' ? ChevronUpIcon : ChevronDownIcon;
  return <DirectionIcon className="size-3.5 text-foreground" />;
};

const HeaderButton = ({
  column,
  label,
  activeColumn,
  direction,
  onSort,
}: {
  column: SortColumn;
  label: string;
  activeColumn?: SortColumn;
  direction?: SortDirection;
  onSort: (column: SortColumn) => void;
}) => (
  <button
    type="button"
    onClick={() => onSort(column)}
    className="inline-flex items-center gap-1 text-left text-sm leading-6 font-medium text-muted-foreground hover:text-foreground"
  >
    {label}
    <SortIndicator active={activeColumn === column} direction={direction} />
  </button>
);

const ContentTitleCell = ({ row }: { row: KnowledgeBaseRow }) => {
  const { t } = useTranslation();
  const content = (
    <>
      <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4">
        {row.icon}
      </span>
      <TruncatedText
        text={row.title ?? t('analytics.deleted_document')}
        size="large"
        className="text-sm leading-5 font-normal text-foreground"
      />
    </>
  );

  if (!row.link) {
    return <div className="flex min-w-0 items-center gap-2">{content}</div>;
  }

  return (
    <Link to={row.link} className="flex min-w-0 items-center gap-2 hover:underline">
      {content}
    </Link>
  );
};

/**
 * Knowledge base performance table.
 *
 * Scoped to the AI Agents analytics page. The numeric "Retrieved" and
 * "Used" columns are sortable client-side; "Content title" and
 * "Knowledge base" are display-only.
 */
export function KnowledgeBasePerformanceTable() {
  const { t } = useTranslation();
  const { data } = useKnowledgeBasePerformance();
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const [sortBy, setSortBy] = useState<SortColumn | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const isEmpty = data.length === 0;

  const rows = useMemo(() => {
    if (!sortBy) {
      return data;
    }
    const next = [...data].sort((a, b) => a[sortBy] - b[sortBy]);
    return sortDirection === 'asc' ? next : next.reverse();
  }, [data, sortBy, sortDirection]);

  const onSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDirection('asc');
  };

  const dateRange = useMemo(() => formatDateRangeLabel(startDate, endDate), [startDate, endDate]);

  return (
    <section className="flex flex-col gap-6 rounded-lg border border-border bg-background p-6">
      <header className="flex items-start gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h2 className="text-base leading-none font-semibold text-foreground">
            Knowledge base performance
          </h2>
          <p className="text-sm leading-5 font-normal text-muted-foreground">{dateRange}</p>
        </div>
        {!isEmpty && <KnowledgeBasePerformanceExport data={data} />}
      </header>
      {isEmpty ? (
        <div className="flex items-center justify-center py-6">
          <AnalyticsEmptyState
            title={t('analytics.no_data_available')}
            description={t('analytics.no_data_for_period')}
          />
        </div>
      ) : (
        <Table className="max-h-96 overscroll-x-none overscroll-y-auto">
          <TableHeader>
            <TableRow className="h-12 hover:bg-transparent">
              <TableHead className="h-12 px-4 py-1">
                <HeaderLabel label="Content title" />
              </TableHead>
              <TableHead className="h-12 px-4 py-1">
                <HeaderLabel label="Knowledge base" />
              </TableHead>
              <TableHead className="h-12 w-32 min-w-24 px-4 py-1">
                <HeaderButton
                  column="retrieved"
                  label="Retrieved"
                  activeColumn={sortBy}
                  direction={sortDirection}
                  onSort={onSort}
                />
              </TableHead>
              <TableHead className="h-12 w-32 min-w-24 px-4 py-1">
                <HeaderButton
                  column="used"
                  label="Used"
                  activeColumn={sortBy}
                  direction={sortDirection}
                  onSort={onSort}
                />
              </TableHead>
              {isComparing && <TableHead className="h-12 w-12 px-4 py-1" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} className="h-14">
                <TableCell className="h-14 max-w-0 px-4">
                  <ContentTitleCell row={row} />
                </TableCell>
                <TableCell className="h-14 max-w-72 truncate px-4 text-sm leading-5 font-normal text-foreground">
                  {row.collection ?? t('analytics.deleted_knowledge_base')}
                </TableCell>
                <TableCell className="h-14 w-32 min-w-24 px-4 text-sm leading-5 font-normal text-foreground tabular-nums">
                  {numberFormatter.format(row.retrieved)}
                </TableCell>
                <TableCell className="h-14 w-32 min-w-24 px-4 text-sm leading-5 font-normal text-foreground tabular-nums">
                  {numberFormatter.format(row.used)}
                </TableCell>
                {isComparing && (
                  <TableCell className="h-14 w-12 px-4">
                    <CompareTrendCell
                      current={row.used}
                      previous={row.previousUsed}
                      formatValue={(value) => numberFormatter.format(value)}
                      thisPeriodLabel={t('analytics.overview_new.used_this_period')}
                      previousPeriodLabel={t('analytics.overview_new.used_previous_period')}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
